import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva } from 'class-variance-authority'
import type { VariantProps } from 'class-variance-authority'

import { cn } from '#/shared/lib/utils'

const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-blue-500 text-white hover:bg-blue-500/90',
        destructive: 'bg-destructive text-white hover:bg-destructive/90',
        outline: 'border border-gray-200 bg-white text-gray-500 hover:bg-gray-50',
        'outline-primary': 'border border-primary bg-white text-primary hover:bg-blue-50',
        secondary: 'bg-gray-50 text-gray-900 hover:bg-gray-100',
        soft: 'bg-blue-100 border border-blue-500 text-blue-500 hover:bg-blue-100/80',
        'soft-destructive': 'bg-red-50 border border-red-100 text-red-500 hover:bg-red-50/80',
        warning: 'bg-orange-500 text-white font-normal hover:bg-orange-500/90',
        ghost: 'hover:bg-gray-50 text-gray-900',
        link: 'text-blue-500 underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-12 px-8 text-base',
        xxs: 'h-6 px-3.5 text-sm',
        xs: 'h-8 px-2 text-sm',
        sm: 'h-9 px-3',
        lg: 'h-12 w-full px-8 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean
  pressEffect?: boolean
  loading?: boolean
}

/** Centered spinner over an invisible label, so the button width never shifts. */
function ButtonSpinner({ children }: { children?: React.ReactNode }) {
  return (
    <>
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
      </span>
      <span className="invisible inline-flex items-center gap-1.5">{children}</span>
    </>
  )
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      pressEffect = false,
      loading,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button'
    // Slot demands a single child, so the spinner overlay only applies to a
    // real <button>. The label keeps its box (invisible, not removed) so the
    // button width never shifts when the spinner appears.
    const showLoading = loading === true && !asChild
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size }),
          pressEffect &&
            'transform-gpu transition-[transform,background-color,color,border-color] duration-150 ease-out active:scale-[0.98]',
          showLoading && 'relative',
          className,
        )}
        ref={ref}
        disabled={disabled === true || showLoading}
        aria-busy={showLoading}
        {...props}
      >
        {showLoading ? <ButtonSpinner>{children}</ButtonSpinner> : children}
      </Comp>
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
